import React, { useState } from "react";
import { TextField, Typography } from "@mui/material";
import InputAdornment from "@mui/material/InputAdornment";
import { Cta } from '../components/Cta.js';
import { ReactComponent as Check } from "../assets/loginPage/check.svg";
import { ReactComponent as Group31 } from "../assets/loginPage/group-31.svg";
import { ReactComponent as Mail } from "../assets/loginPage/mail.svg";
import maskGroup from "../assets/loginPage/mask-group.png";
import "../css/AuthPages.css";
import { useNavigate } from 'react-router-dom';
import { auth } from '../firebase';
import { sendPasswordResetEmail } from "firebase/auth";
import { createPopup } from '../utils/popupUtils';
import { StyledEngineProvider } from "@mui/material";
import CssBaseline from "@mui/material/CssBaseline";
import Box from "@mui/material/Box";

/* Forgot password. Firebase mints and emails the reset link; the link lands on
ResetPasswordPage via the custom action URL configured in the Firebase console. */

const ForgotPasswordPage = () => {
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        email: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevState => ({
            ...prevState,
            [name]: value
        }));
    };

    const BASE_URL = window.location.hostname === 'localhost'
        ? 'http://localhost:3000'
        : 'https://joynet-99b18.web.app';

    const actionCodeSettings = {
        // Where Firebase sends the user once the reset is complete. The domain
        // must be in the authorized domains list in the Firebase Console.
        url: `${BASE_URL}/login`,
        handleCodeInApp: false,
    };

    // Shown whether or not the address is registered, so the form can't be used
    // to probe which emails have accounts.
    const GENERIC_SENT_MESSAGE = "If an account exists for that email, a reset link is on its way. Check your inbox and spam folder.";

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (isSubmitting) {
            return;
        }
        setIsSubmitting(true);

        try {
            await sendPasswordResetEmail(auth, formData.email, actionCodeSettings);
            createPopup(GENERIC_SENT_MESSAGE, 'success', { gradient: true, duration: 6000 });
        } catch (error) {
            console.error('Password reset request error:', error);

            // Don't reveal that the address has no account — same message as success.
            if (error.code === 'auth/user-not-found') {
                createPopup(GENERIC_SENT_MESSAGE, 'success', { gradient: true, duration: 6000 });
                return;
            }

            const friendlyMessages = {
                'auth/invalid-email': 'The email address is not valid.',
                'auth/missing-email': 'Please enter your email address.',
                'auth/user-disabled': 'This account has been disabled. Please contact support.',
                'auth/too-many-requests': 'Too many requests — please wait a few minutes before trying again.',
                'auth/network-request-failed': 'Network error. Please check your connection and try again.',
            };
            createPopup(
                friendlyMessages[error.code] || "Something went wrong. Please try again.",
                'error',
                { gradient: true, duration: 4000 }
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
    <StyledEngineProvider injectFirst>
    <CssBaseline />
    <div className="auth-page">
        <div className="left-panel">
            <div className="logo-container">
                <Group31 className="group responsive-img" />
                <div>
                    <span className="joynet-text">JoyNet</span>
                </div>
            </div>

            <Typography variant="subtitle2" color="primary" className="tagline">
                WELCOME TO YOUR SPACE OF HEALING AND JOY
            </Typography>
            <Typography variant="h4" className="title-text">
                Forgot Your Password? Let's Get You Back to Joy
            </Typography>
            <Box
                component="form"
                className="signup-form"
                onSubmit={handleSubmit}
            >
                {/* Not .sign-up-text: that class shifts by translateX(-7%), which
                    clips multi-line copy off the left edge on narrow screens. */}
                <Typography variant="body2" align="center">
                    Enter the email you signed up with and we'll send you a link to reset your password.
                </Typography>

                <TextField
                    name="email"
                    label="Email"
                    variant="filled"
                    value={formData.email}
                    onChange={handleChange}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Mail className="input-icon" />
                            </InputAdornment>
                        ),
                        disableUnderline: true
                    }}
                />

                <Cta text={isSubmitting ? "Sending..." : "Send Reset Link"} type="submit" disabled={isSubmitting} />

                <Typography variant="body2" className="sign-up-text">
                    Remembered it?&nbsp;
                    <span className="login-link" onClick={() => navigate('/login')}>
                        Log In
                    </span>
                </Typography>
            </Box>
        </div>

        <div className="right-panel">
            <div className="benefit-card">
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Check className="benefit-icon" />
                    <div>
                        <div className="benefit-title">Begin Your Journey to Healing</div>
                        <div className="benefit-description">
                            Find solace and connection in our community. The JoyNet App is a sanctuary where conversations heal and uplift.
                        </div>
                    </div>
                </div>
            </div>

            <div className="benefit-card">
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Check className="benefit-icon" />
                    <div>
                        <div className="benefit-title">Simplicity in Connection, Richness in Experience</div>
                        <div className="benefit-description">
                            Signing up is just the start. Join chatrooms, connect with kindred spirits, and share in the celebration of Black Joy.
                        </div>
                    </div>
                </div>
            </div>
            <img src={maskGroup} alt="Illustration" className="auth-image" />
        </div>
    </div>
    </StyledEngineProvider>
    );
};

export default ForgotPasswordPage;
